import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { profileApi } from '@internal/api/profile';
import {
  clearEnrichmentJob,
  rememberEnrichmentJob,
} from '@internal/components/app/components/enrichment-watcher';
import FieldStyles from '@internal/components/app/components/field-styles/field-styles';
import Logo from '@internal/components/app/components/side-nav/lemon-logo.svg';
import { UtmContextProvider } from '@internal/components/escape-the-matrix/utm-context';
import Routes from '@internal/config/routes';
import {
  getSafeRedirectPath,
  REDIRECT_QUERY_PARAM,
} from '@internal/utils/safe-redirect';

import AccountStep from './account-step';
import BasicsStep from './basics-step';
import ImportStep from './import-step';
import { normalizeUrl } from './linkedin';
import ParsingStep from './parsing-step';
import {
  ContentCard,
  ContentColumn,
  PageRoot,
  StepPane,
  TopBar,
} from './signup.styled';

type SignupStep = 'account' | 'import' | 'basics' | 'parsing';

type SignupPageProps = {
  resumeStep?: Extract<SignupStep, 'import' | 'basics'>;
  resumeApiKey?: string;
  resumeQualified?: boolean;
  resumeLinkedin?: string;
  resumeGithub?: string;
};

const SignupPage: React.FC<SignupPageProps> = ({
  resumeStep,
  resumeApiKey = '',
  resumeQualified = true,
  resumeLinkedin = '',
  resumeGithub = '',
}) => {
  const router = useRouter();
  const [step, setStep] = useState<SignupStep>(resumeStep ?? 'account');
  const [apiKey, setApiKey] = useState(resumeStep ? resumeApiKey : '');
  const [qualified, setQualified] = useState(
    resumeStep ? resumeQualified : true
  );
  const [linkedin, setLinkedin] = useState(resumeStep ? resumeLinkedin : '');
  const [parsingStarted, setParsingStarted] = useState(false);

  useEffect(() => {
    if (!resumeStep) return;
    setStep(resumeStep);
    setApiKey(resumeApiKey);
    setQualified(resumeQualified);
    setLinkedin(resumeLinkedin);
  }, [resumeStep, resumeApiKey, resumeQualified, resumeLinkedin]);

  const { updateProfileAsync } = profileApi.updateProfile.useMutation();
  const { triggerEnrichmentAsync } = profileApi.triggerEnrichment.useMutation();

  const startParsing = async (
    key: string,
    isQualified: boolean,
    linkedinValue: string
  ) => {
    const linkedinUrl = normalizeUrl(linkedinValue);
    try {
      await updateProfileAsync({ apiKey: key, linkedin: linkedinUrl });
      if (!isQualified) return;

      rememberEnrichmentJob({ apiKey: key, jobId: null, storedAt: Date.now() });
      const response = await triggerEnrichmentAsync({
        apiKey: key,
        linkedinUrl,
      });
      const jobId = response?.profileEnrichmentJob?.id;
      if (jobId) {
        rememberEnrichmentJob({ apiKey: key, jobId, storedAt: Date.now() });
      } else {
        clearEnrichmentJob();
      }
      setParsingStarted(true);
    } catch {
      // Silent: signup already succeeded; the import step retries enrichment.
    }
  };

  const handleRegistered = (
    key: string,
    isQualified: boolean,
    linkedinValue: string
  ) => {
    setApiKey(key);
    setQualified(isQualified);
    setLinkedin(linkedinValue);
    setStep('import');
    if (linkedinValue) void startParsing(key, isQualified, linkedinValue);
  };

  let content: React.ReactNode;
  if (step === 'account') {
    content = <AccountStep onRegistered={handleRegistered} />;
  } else if (step === 'import' || step === 'basics') {
    // Both steps stay mounted so the entered values survive Back/Continue.
    content = (
      <>
        <StepPane $hidden={step !== 'import'}>
          <ImportStep
            apiKey={apiKey}
            qualified={qualified}
            initialLinkedin={linkedin}
            initialGithub={resumeStep ? resumeGithub : ''}
            parsingStarted={Boolean(resumeStep) || parsingStarted}
            onContinue={() => setStep('basics')}
          />
        </StepPane>
        <StepPane $hidden={step !== 'basics'}>
          <BasicsStep
            apiKey={apiKey}
            onBack={() => setStep('import')}
            onFinish={() => setStep('parsing')}
          />
        </StepPane>
      </>
    );
  } else {
    const doneTarget =
      getSafeRedirectPath(router.query[REDIRECT_QUERY_PARAM]) ??
      Routes.GetSetUp;
    content = (
      <ParsingStep
        apiKey={apiKey}
        onDone={() => void router.replace(doneTarget)}
      />
    );
  }

  return (
    <UtmContextProvider>
      <PageRoot>
        <FieldStyles />
        <TopBar>
          <Image src={Logo as string} alt="Lemon.io" width={112} height={32} />
        </TopBar>
        <ContentColumn>
          <ContentCard>{content}</ContentCard>
        </ContentColumn>
      </PageRoot>
    </UtmContextProvider>
  );
};

export default SignupPage;
